import { Rol } from "@copi/shared";
import {
  BarChart3,
  Building2,
  CalendarDays,
  FileText,
  Gavel,
  GraduationCap,
  Inbox,
  Landmark,
  LayoutDashboard,
  Palette,
  UserPlus,
  Users,
} from "lucide-react";
import { NavLink } from "react-router-dom";

import { SidebarHeader } from "./SidebarHeader";

import { paths } from "@/app/paths";
import { cn } from "@/lib/utils/cn";

/** Contexto del usuario para decidir visibilidad de cada item (rol + capacidades, ADR 054). */
interface NavContexto {
  rol: Rol;
  puedeAsesorar: boolean;
  puedeSerJurado: boolean;
}

interface NavItem {
  to: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  rolesPermitidos?: Rol[];
  /**
   * Predicado de visibilidad por CAPACIDAD (ADR 054). Tiene prioridad sobre
   * `rolesPermitidos` cuando está presente. Para items "de docente" cuyo acceso
   * depende de `puedeAsesorar`/`puedeSerJurado`, no del rol.
   */
  visible?: (ctx: NavContexto) => boolean;
  /**
   * Encabezado de sección al que pertenece el item. Se renderiza una vez,
   * antes del primer item visible de la sección. Los items de una misma
   * sección deben ir contiguos en `NAV_ITEMS`.
   */
  seccion?: string;
  /**
   * Contador de pendientes a mostrar como badge (ADR 054). Identifica de qué
   * contador del docente tomar el número (`asesor`/`jurado`); el badge sólo
   * aparece si el número es > 0. Se alimenta del SSE vía `useContadoresDocencia`.
   */
  badge?: "asesor" | "jurado";
}

/**
 * Sidebar de navegación administrativa (Decanato, Asesor, Jurado,
 * Repositorio, Admin). NO se renderiza para roles ESTUDIANTE/BACHILLER
 * — esos usan `SidebarProcesoTesis` (ADR 016).
 *
 * Items filtrados por rol. Cada item declara `rolesPermitidos`; sin
 * declararlo significa "todos".
 */
const NAV_ITEMS: NavItem[] = [
  { to: paths.dashboard(), label: "Dashboard", icon: LayoutDashboard },
  // ADR 020 + ADR 054: "Expedientes" es la vista institucional (todos los
  // expedientes de la facultad) — sólo DECANATO. Para un docente sería redundante
  // con "Asesorías" ∪ "Jurado" (la unión de sus dos contextos), así que NO se
  // muestra: el docente navega por sus dos sombreros en la sección "Docencia".
  {
    to: "/expedientes",
    label: "Expedientes",
    icon: FileText,
    visible: (c) => c.rol === Rol.DECANATO,
  },
  // ADR 023: el item antes se llamaba "Decanato" — auto-referencial para
  // el usuario que ES el decano. Renombrado a "Pendientes" (acción que se
  // hace adentro: revisar solicitudes y conformar jurados). URL `/decanato`
  // se mantiene como namespace estable.
  {
    to: "/decanato",
    label: "Pendientes",
    icon: Inbox,
    rolesPermitidos: [Rol.DECANATO, Rol.ADMIN],
  },
  {
    to: "/decanato/reportes",
    label: "Reportes",
    icon: BarChart3,
    rolesPermitidos: [Rol.DECANATO, Rol.ADMIN],
  },
  // ADR 054 — sección "Docencia": los DOS sombreros del docente como items
  // separados y claramente etiquetados (asesor vs jurado), gateados por capacidad.
  // Iconos distintos para leer el rol de un vistazo: birrete = asesor, mazo = jurado.
  {
    to: "/asesoria",
    label: "Asesorías",
    icon: GraduationCap,
    seccion: "Docencia",
    visible: (c) => c.puedeAsesorar,
    badge: "asesor",
  },
  {
    to: "/jurado",
    label: "Jurado",
    icon: Gavel,
    seccion: "Docencia",
    visible: (c) => c.puedeSerJurado,
    badge: "jurado",
  },
  // ADR 020: "Repositorio" como item del sidebar fue eliminado — el rol
  // REPOSITORIO ya ve `RepositorioDashboard` en `/`, así que un link
  // adicional a `/repositorio` (placeholder vacío) era redundancia
  // confusa. Para ADMIN, el dashboard también funciona.
  {
    to: "/admin/feriados",
    label: "Feriados",
    icon: CalendarDays,
    rolesPermitidos: [Rol.ADMIN, Rol.DECANATO],
  },
  // ADR 055 — estructura académica del tenant (facultades y escuelas/EAPs), sólo ADMIN.
  {
    to: "/admin/academico",
    label: "Estructura académica",
    icon: Landmark,
    rolesPermitidos: [Rol.ADMIN],
  },
  // ADR 054 — gestión de docentes y sus capacidades (asesor/jurado), sólo ADMIN.
  {
    to: "/admin/usuarios",
    label: "Docentes",
    icon: Users,
    rolesPermitidos: [Rol.ADMIN],
  },
  // ADR 055 — alta individual de estudiantes, sólo ADMIN.
  {
    to: "/admin/estudiantes",
    label: "Estudiantes",
    icon: UserPlus,
    rolesPermitidos: [Rol.ADMIN],
  },
  // ADR 047 — personalización (white-label) de la propia institución, sólo ADMIN.
  {
    to: "/admin/personalizacion",
    label: "Personalización",
    icon: Palette,
    rolesPermitidos: [Rol.ADMIN],
  },
  // ADR 045 — administración de plataforma (tenants), sólo SUPER_ADMIN.
  {
    to: "/admin/instituciones",
    label: "Instituciones",
    icon: Building2,
    rolesPermitidos: [Rol.SUPER_ADMIN],
  },
];

interface SidebarNavegacionProps {
  rol: Rol;
  puedeAsesorar?: boolean;
  puedeSerJurado?: boolean;
  /** Contadores de pendientes para los badges (ADR 054). */
  revisionesAsesor?: number;
  revisionesJurado?: number;
  /** Punto "nuevo" (notificación no leída del rol) por item (ADR 054). */
  nuevoAsesor?: boolean;
  nuevoJurado?: boolean;
}

export function SidebarNavegacion({
  rol,
  puedeAsesorar = false,
  puedeSerJurado = false,
  revisionesAsesor = 0,
  revisionesJurado = 0,
  nuevoAsesor = false,
  nuevoJurado = false,
}: SidebarNavegacionProps) {
  const ctx: NavContexto = { rol, puedeAsesorar, puedeSerJurado };
  const contadorDe = (badge: NavItem["badge"]): number =>
    badge === "asesor" ? revisionesAsesor : badge === "jurado" ? revisionesJurado : 0;
  const esNuevoDe = (badge: NavItem["badge"]): boolean =>
    badge === "asesor" ? nuevoAsesor : badge === "jurado" ? nuevoJurado : false;
  const items = NAV_ITEMS.filter((item) => {
    if (item.visible) return item.visible(ctx);
    if (!item.rolesPermitidos) return true;
    return item.rolesPermitidos.includes(rol);
  });

  // El encabezado de una sección se emite sólo antes de su primer item VISIBLE
  // (si la sección quedó vacía tras el filtrado, no aparece).
  let seccionActual: string | undefined;

  return (
    <aside className="sidebar-surface hidden w-64 flex-col border-r border-border bg-card md:flex">
      <SidebarHeader />
      <nav className="flex-1 space-y-1 p-4" aria-label="Navegación principal">
        {items.map((item) => {
          const mostrarEncabezado = item.seccion && item.seccion !== seccionActual;
          seccionActual = item.seccion;
          const pendientes = contadorDe(item.badge);
          const nuevo = esNuevoDe(item.badge);
          return (
            <div key={item.to}>
              {mostrarEncabezado ? (
                <p className="px-3 pb-1 pt-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground/70">
                  {item.seccion}
                </p>
              ) : null}
              <NavLink
                to={item.to}
                end={item.to === paths.dashboard()}
                className={({ isActive }) =>
                  cn(
                    "flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors",
                    isActive
                      ? "bg-accent text-accent-foreground font-medium"
                      : "text-muted-foreground hover:bg-accent hover:text-accent-foreground",
                  )
                }
              >
                <span className="relative shrink-0">
                  <item.icon className="h-4 w-4" aria-hidden="true" />
                  {nuevo ? (
                    <span
                      className="absolute -right-1 -top-1 h-2 w-2 rounded-full bg-state-vencido ring-2 ring-card"
                      aria-hidden="true"
                    />
                  ) : null}
                </span>
                <span className="flex-1 truncate">{item.label}</span>
                {nuevo ? <span className="sr-only">(novedades)</span> : null}
                {pendientes > 0 ? (
                  <span
                    className="flex h-5 min-w-5 items-center justify-center rounded-full bg-state-en-revision px-1.5 text-[11px] font-bold text-white"
                    aria-label={`${pendientes} pendiente${pendientes === 1 ? "" : "s"}`}
                    title={`${pendientes} pendiente${pendientes === 1 ? "" : "s"}`}
                  >
                    {pendientes > 9 ? "9+" : pendientes}
                  </span>
                ) : null}
              </NavLink>
            </div>
          );
        })}
      </nav>
    </aside>
  );
}
